import React, { Fragment, useState, useEffect } from "react";
import {
  CheckIcon,
  CogIcon,
  DotsHorizontalIcon,
  DownloadIcon,
  PlusIcon,
  SearchIcon,
} from "@heroicons/react/solid";
import {
  Form,
  Button,
  InputGroup,
  Dropdown,
  Nav,
  Card,
  Table,
  Pagination,
  Col,
  Row,
  ButtonGroup,
  Modal,
  ToastContainer,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  useDnldSenderListsMutation,
  useSenderListsMutation,
  useUpdateSenderIDMutation,
} from "Services/services";
import { Link } from "react-router-dom";
import { Routes } from "routes";
import { FcCancel } from "react-icons/fc";
import { downloadBlobFile } from "components/Utility";
import "react-phone-input-2/lib/style.css";
import { Toast } from "react-bootstrap";
import hasPermission from "pages/Permissions";

export default () => {
  // Get the user type from localStorage :: UI
  const userData = localStorage.getItem("TOKEN");
  const userObject = JSON.parse(userData);
  const userType = userObject.user_type;
  const userID = userObject.id;

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState("success");

  const [tableData, setTableData] = useState([]);

  // const [bulkOption, setBulkOption] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [handleReject, setHandleReject] = useState(false);
  const [toUpdate, setToUpdate] = useState({
    sender_id: null,
    status: null,
    remarks: null,
  });

  const [pagelimit, setPagelimit] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalPosts, setTotalPosts] = useState(0);
  const [debouncedValue, setDebouncedValue] = useState("");

  const [SenderList] = useSenderListsMutation();
  const [update] = useUpdateSenderIDMutation();
  const [download] = useDnldSenderListsMutation();

  // Debounce search value
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(searchValue); // Set debounced value after 3 seconds
    }, 2000);
    // Cleanup the timeout if searchValue changes (to avoid multiple timeouts)
    return () => {
      clearTimeout(handler);
    };
  }, [searchValue]);

  const fetchTableData = async () => {
    try {
      const response = await SenderList({
        page: currentPage,
        limit: pagelimit,
        search: searchValue,
      });
      if (response?.data?.statusCode === 200) {
        setTableData(response?.data?.data?.items || []);
        setTotalPages(Math.ceil(response?.data?.data?.totalItems / pagelimit));
        setTotalPosts(response?.data?.data?.totalItems || 0);
      } else {
        setTableData([]);
        setTotalPages(0);
        setTotalPosts(0);
      }
    } catch (error) {
      alert(error.message);
      setTableData([]);
      setTotalPages(0);
      setTotalPosts(0);
    }
  };

  useEffect(() => {
    fetchTableData();
    // eslint-disable-next-line
  }, [currentPage, pagelimit, debouncedValue]);

  const changeSearchValue = (e) => {
    setSearchValue(e.target.value);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await download({
        user_id:
          userType === "super_admin" || userType === "admin" ? "" : userID,
        user_type_id: userObject.user_type_id,
        search: searchValue,
      }).unwrap();
      downloadBlobFile(blob, "SenderID_List.csv");
    } catch (err) {
      if (err?.status === 404) alert("No data found.");
      else alert("Download failed.");
    }
  };

  const updateStatus = (status, props) => {
    const updateObj = {
      sender_id: props.id,
      status: status,
      remarks: "",
    };
    setToUpdate(updateObj);

    if (status === "reject") setHandleReject(true);
    else submitStatus(updateObj);
  };

  const handleClose = () => {
    setHandleReject(false);
  };

  const submitStatus = (data) => {
    handleClose();
    update({
      senderId: data.sender_id,
      action: data.status,
      remark: data.remarks,
    })
      .then((success) => {
        if (success?.data?.statusCode === 200) {
          setToastMessage(success?.data?.message);
          setToastVariant("success");
          setTimeout(() => {
            fetchTableData();
          }, 500);
        } else if (success?.data?.statusCode > 300) {
          setToastMessage(
            `Error: ${success?.data?.message || "Upload failed!"}`
          );
          setToastVariant("danger");
        }
        setShowToast(true);
      })
      .catch((error) => {
        console.error("Upload failed", error);
        setToastMessage(`Error: ${error?.message || "Upload failed!"}`);
        setToastVariant("danger");
        setShowToast(true);
      });
  };

  const TableRow = (props) => {
    const statusVariant =
      props.status === "approved"
        ? "success"
        : props.status === "pending"
        ? "purple"
        : props.status === "rejected"
        ? "danger"
        : props.status === "paused"
        ? "warning"
        : "primary";

    return (
      <tr className="border-bottom">
        <td>
          <span title={props.sender_id || "-"} className="fw-normal">
            {props.sender_id || "-"}
          </span>
        </td>
        <td>
          <span title={props.country || "-"} className="fw-normal">
            {props.country || "-"}
          </span>
        </td>
        <td>
          <span title={props.category || "-"} className="fw-normal">
            {props.category || "-"}
          </span>
        </td>
        {userType !== "enterprise" && (
          <td>
            <span title={props.enterprise_name || "-"} className="fw-normal">
              {props.enterprise_name || "-"}
            </span>
          </td>
        )}
        <td>
          <span
            title={props.status || "-"}
            className={`fw-normal text-${statusVariant} text-capitalize`}
          >
            {props.status || "-"}
          </span>
        </td>
        <td>
          <span title={props.remark || "-"} className="fw-normal">
            {props.remark || "-"}
          </span>
        </td>
        {(userType === "super_admin" || userType === "admin") && (
          <td>
            <div className="d-flex align-items-center">
              {(props.status === "rejected" || props.status === "pending") && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip className="m-0">Approve</Tooltip>}
                >
                  <Card.Link onClick={() => updateStatus("approve", props)}>
                    <CheckIcon className="icon icon-xxs text-success" />
                  </Card.Link>
                </OverlayTrigger>
              )}

              {(props.status === "approved" || props.status === "pending" )&& (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip className="m-0">Reject</Tooltip>}
                >
                  <Card.Link onClick={() => updateStatus("reject", props)}>
                    <FcCancel className="icon icon-xxs text-danger" />
                  </Card.Link>
                </OverlayTrigger>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  const canEdit = hasPermission("SenderID Registration", "edit");
  const canDownload = hasPermission("SenderID Registration", "download");

  return (
    <Fragment>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          bg={toastVariant}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Header>
            <strong className="me-auto">Notification</strong>
          </Toast.Header>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <Card border="1">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <InputGroup className="d-flex flex-nowrap mb-3 fmxw-300">
            <InputGroup.Text>
              <SearchIcon className="icon icon-xs" />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search Sender-ID"
              value={searchValue}
              onChange={changeSearchValue}
            />
          </InputGroup>

          <div className="ms-auto d-inline-flex gap-2 mb-3">
            {canEdit && (
              <Button
                as={Link}
                to={Routes.CreateSenderId.path}
                variant="secondary"
                size="sm"
                className="ms-auto d-inline-flex gap-2 align-items-center"
              >
                <PlusIcon className="icon icon-xs" /> New Sender-ID
              </Button>
            )}

            {canDownload && (
              <Button
                variant="outline-gray-300"
                className="p-2 d-flex align-items-center"
                onClick={handleDownload}
              >
                <DownloadIcon className="icon icon-sm" />
              </Button>
            )}

            <Dropdown className="">
              <Dropdown.Toggle
                split
                as={Button}
                variant="outline-gray-300"
                className="text-dark m-0 p-2"
              >
                <CogIcon className="icon icon-sm" />
                <span className="visually-hidden">Toggle Dropdown</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="dropdown-menu-xs dropdown-menu-end p-2">
                <span className="small ps-2 fw-bold text-dark">Rows per page</span>
                {[10, 20, 30].map((limit) => (
                  <Dropdown.Item
                    key={limit}
                    className={`d-flex align-items-center fw-bold ${
                      limit === 30 ? "rounded-bottom" : ""
                    }`}
                    onClick={() => {
                      setPagelimit(limit); // Update the page limit
                      setCurrentPage(1); // Reset the page number to 1
                    }}
                  >
                    {limit}{" "}
                    {pagelimit === limit && (
                      <CheckIcon className="icon icon-xxs ms-auto" />
                    )}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="custom-scroll-wrapper">
          <div className="scroll-container">
            <Table
              hover
              responsive
              className="align-items-center table-flush table-outlines"
            >
              <thead className="thead-light">
                <tr>
                  <th className="border-bottom">Sender ID</th>
                  <th className="border-bottom">Country</th>
                  <th className="border-bottom">Category</th>
                  {userType !== "enterprise" && (
                    <th className="border-bottom">Enterprise</th>
                  )}
                  <th className="border-bottom">Status</th>
                  <th className="border-bottom">Remarks</th>
                  {(userType === "super_admin" || userType === "admin") && (
                    <th className="border-bottom">Action</th>
                  )}
                </tr>
              </thead>
              <tbody className="border-0">
                {tableData.length > 0 ? (
                  tableData.map((u, index) => (
                    <TableRow key={index + 1} index={index + 1} {...u} />
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="text-center text-danger">
                      No Data Found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </div>
        <Card.Footer className="p-0 mt-3 border-0 d-flex flex-wrap align-items-center justify-content-between">
          <Nav>
            <Pagination className="mb-0">
              <Pagination.Prev
                onClick={handlePrev}
                disabled={currentPage === 1}
              >
                Previous
              </Pagination.Prev>
              {currentPage >= 3 && (
                <>
                  <Pagination.Item onClick={() => handlePageChange(1)}>
                    1
                  </Pagination.Item>
                  <Pagination.Ellipsis />
                </>
              )}
              {currentPage > 1 && (
                <Pagination.Item
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  {currentPage - 1}
                </Pagination.Item>
              )}
              <Pagination.Item active>{currentPage}</Pagination.Item>
              {currentPage < totalPages && (
                <Pagination.Item
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {currentPage + 1}
                </Pagination.Item>
              )}
              {currentPage < totalPages - 1 && (
                <>
                  <Pagination.Ellipsis />
                  <Pagination.Item onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                  </Pagination.Item>
                </>
              )}
              <Pagination.Next
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                Next
              </Pagination.Next>
            </Pagination>
          </Nav>
          <small className="fw-normal small mt-2 mt-md-0">
            Showing{" "}
            <b>
              {totalPosts == 0 ? totalPosts : pagelimit * (currentPage - 1) + 1}
              -
              {pagelimit * currentPage > totalPosts
                ? totalPosts
                : pagelimit * currentPage}
            </b>{" "}
            of <b>{totalPosts}</b> entries.
          </small>
        </Card.Footer>
      </Card>

      <Modal
        backdrop="static"
        keyboard={false}
        as={Modal.Dialog}
        centered
        show={handleReject}
        onHide={handleClose}
      >
        <Modal.Header>
          <Modal.Title className="h6">Reject Remarks</Modal.Title>
          <Button variant="close" aria-label="Close" onClick={handleClose} />
        </Modal.Header>
        <Modal.Body style={{ width: "auto" }}>
          <Row className="p-2">
            <Col md={12}>
              <Form.Group id="sender">
                <Form.Label>
                  Remarks <span className="text-danger"> * </span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  as="textarea"
                  placeholder="Enter comment here."
                  className="mb-0"
                  value={toUpdate.remarks}
                  onChange={(e) => {
                    setToUpdate((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }));
                  }}
                  autoComplete="remarks"
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => submitStatus(toUpdate)}>
            Submit
          </Button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};
